import React, { createContext, useContext, useReducer, useEffect } from "react";

// Initial state
const initialState = {
  highlights: [],
  suggestions: [],
  tasks: [],
  pageLinks: [],
  guidedQuestions: [],
  currentPage: null,
  aiResponse: null,
};

// Action types
const ACTIONS = {
  ADD_HIGHLIGHT: "ADD_HIGHLIGHT",
  REMOVE_HIGHLIGHT: "REMOVE_HIGHLIGHT",
  SET_SUGGESTIONS: "SET_SUGGESTIONS",
  ADD_TASK: "ADD_TASK",
  COMPLETE_TASK: "COMPLETE_TASK",
  SET_PAGE_LINKS: "SET_PAGE_LINKS",
  SET_GUIDED_QUESTIONS: "SET_GUIDED_QUESTIONS",
  SET_CURRENT_PAGE: "SET_CURRENT_PAGE",
  SET_AI_RESPONSE: "SET_AI_RESPONSE",
};

// Reducer
function aiSidebarReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_HIGHLIGHT:
      return {
        ...state,
        highlights: [
          ...state.highlights,
          {
            id: Date.now(),
            text: action.payload.text,
            timestamp: action.payload.timestamp,
            duration: action.payload.duration,
            tags: action.payload.tags || [],
          },
        ],
      };

    case ACTIONS.REMOVE_HIGHLIGHT:
      return {
        ...state,
        highlights: state.highlights.filter((h) => h.id !== action.payload),
      };

    case ACTIONS.SET_SUGGESTIONS:
      return {
        ...state,
        suggestions: action.payload,
      };

    case ACTIONS.ADD_TASK:
      return {
        ...state,
        tasks: [
          ...state.tasks,
          {
            id: Date.now(),
            text: action.payload.text,
            dueDate: action.payload.dueDate,
            priority: action.payload.priority,
            completed: false,
            source: action.payload.source || "manual",
          },
        ],
      };

    case ACTIONS.COMPLETE_TASK:
      return {
        ...state,
        tasks: state.tasks.map((task) =>
          task.id === action.payload ? { ...task, completed: true } : task,
        ),
      };

    case ACTIONS.SET_PAGE_LINKS:
      return {
        ...state,
        pageLinks: action.payload,
      };

    case ACTIONS.SET_GUIDED_QUESTIONS:
      return {
        ...state,
        guidedQuestions: action.payload,
      };

    case ACTIONS.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: action.payload,
      };

    case ACTIONS.SET_AI_RESPONSE:
      return {
        ...state,
        aiResponse: action.payload,
      };

    default:
      return state;
  }
}

// Create context
const AISidebarContext = createContext();

// Provider component
export function AISidebarProvider({ children }) {
  const [state, dispatch] = useReducer(aiSidebarReducer, initialState);

  // Add highlight from transcript
  const addHighlight = (text, duration, tags = []) => {
    dispatch({
      type: ACTIONS.ADD_HIGHLIGHT,
      payload: {
        text,
        timestamp: new Date().toISOString(),
        duration,
        tags,
      },
    });
  };

  // Add task (manual or AI-suggested)
  const addTask = (
    text,
    dueDate = null,
    priority = "medium",
    source = "manual",
  ) => {
    dispatch({
      type: ACTIONS.ADD_TASK,
      payload: { text, dueDate, priority, source },
    });
  };

  // Complete task
  const completeTask = (taskId) => {
    dispatch({
      type: ACTIONS.COMPLETE_TASK,
      payload: taskId,
    });
  };

  // Update AI suggestions based on transcript
  const updateSuggestions = (transcript) => {
    // Here you would integrate with your AI service to analyze the transcript
    // and generate appropriate suggestions
    // For now, we'll use placeholder logic
    const suggestions = [];

    // Example: Detect dates and suggest tasks
    const datePattern =
      /\b(?:next|this)?\s*(?:week|month|year)\b|\b\d{1,2}\/\d{1,2}\/\d{4}\b/gi;
    const dates = transcript.match(datePattern);
    if (dates) {
      suggestions.push({
        type: "task",
        text: `Schedule follow-up for ${dates[0]}`,
        priority: "medium",
      });
    }

    dispatch({
      type: ACTIONS.SET_SUGGESTIONS,
      payload: suggestions,
    });
  };

  // Update page links based on conversation context
  const updatePageLinks = (transcript) => {
    // Here you would analyze the transcript and suggest relevant pages
    // For now, we'll use placeholder logic
    const pageLinks = [];

    // Example: Detect investment-related keywords
    if (/\b(?:portfolio|investment|stock|bond|fund)\b/i.test(transcript)) {
      pageLinks.push({
        title: "Market Research",
        path: "/portfolio-management/market-research",
        reason: "Investment topics discussed",
      });
    }

    dispatch({
      type: ACTIONS.SET_PAGE_LINKS,
      payload: pageLinks,
    });
  };

  // Get AI response for questions
  const getAIResponse = async (question) => {
    // Here you would integrate with your AI service to get responses
    // For now, we'll use a placeholder
    const response = {
      answer:
        "This is a placeholder AI response. Integrate with your AI service for real answers.",
      confidence: 0.85,
    };

    dispatch({
      type: ACTIONS.SET_AI_RESPONSE,
      payload: response,
    });
  };

  const value = {
    state,
    addHighlight,
    addTask,
    completeTask,
    updateSuggestions,
    updatePageLinks,
    getAIResponse,
  };

  return (
    <AISidebarContext.Provider value={value}>
      {children}
    </AISidebarContext.Provider>
  );
}

// Custom hook
export function useAISidebar() {
  const context = useContext(AISidebarContext);
  if (!context) {
    throw new Error("useAISidebar must be used within an AISidebarProvider");
  }
  return context;
}
